import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Building2, 
  MapPin, 
  CheckCircle2, 
  AlertCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { TRN, Branch } from '../../types';
import { UAE_EMIRATES } from '../../constants';

interface ClientTRNManagementProps {
  clientId: string;
  clientName: string;
}

const mockClientTRNs: TRN[] = [
  {
    id: 'trn-001',
    trnNumber: '100123456700003',
    legalEntityName: 'ABC Trading LLC',
    status: 'active',
    registrationDate: '2020-01-15',
    branches: [
      {
        id: 'branch-001',
        branchCode: 'DXB-001',
        branchName: 'Dubai Main Office',
        address: 'Sheikh Zayed Road, Trade Centre 1',
        city: 'Dubai',
        emirate: 'Dubai',
        status: 'active',
        manager: 'Ahmed Al Mansoori'
      },
      {
        id: 'branch-002',
        branchCode: 'DXB-002',
        branchName: 'Dubai Marina Branch',
        address: 'Dubai Marina Mall, Ground Floor',
        city: 'Dubai',
        emirate: 'Dubai',
        status: 'active',
        manager: 'Fatima Al Zarooni'
      },
      {
        id: 'branch-003',
        branchCode: 'SHJ-001',
        branchName: 'Sharjah Branch',
        address: 'Al Khan Area, Office 205',
        city: 'Sharjah',
        emirate: 'Sharjah',
        status: 'active'
      }
    ]
  },
  {
    id: 'trn-002',
    trnNumber: '100234567800004',
    legalEntityName: 'XYZ Electronics Trading LLC',
    status: 'active',
    registrationDate: '2021-03-20',
    branches: [
      {
        id: 'branch-004',
        branchCode: 'AUH-001',
        branchName: 'Abu Dhabi Headquarters',
        address: 'Corniche Road, Al Markaziyah',
        city: 'Abu Dhabi',
        emirate: 'Abu Dhabi',
        status: 'active',
        manager: 'Mohammed Al Blooshi'
      }
    ]
  }
];

export default function ClientTRNManagement({ clientId, clientName }: ClientTRNManagementProps) {
  const [trns, setTrns] = useState<TRN[]>(mockClientTRNs);
  const [isAddTRNDialogOpen, setIsAddTRNDialogOpen] = useState(false);
  const [isEditTRNDialogOpen, setIsEditTRNDialogOpen] = useState(false);
  const [isAddBranchDialogOpen, setIsAddBranchDialogOpen] = useState(false);
  const [selectedTRNId, setSelectedTRNId] = useState<string | null>(null);
  const [expandedTRNId, setExpandedTRNId] = useState<string | null>(null);
  
  const [newTRN, setNewTRN] = useState({
    trnNumber: '',
    legalEntityName: '',
    registrationDate: new Date().toISOString().split('T')[0],
    status: 'active' as 'active' | 'inactive' | 'suspended'
  });

  const [newBranch, setNewBranch] = useState({
    branchCode: '',
    branchName: '',
    address: '',
    city: '',
    emirate: 'Dubai',
    manager: '',
    status: 'active' as 'active' | 'inactive'
  });

  const handleAddTRN = () => {
    if (!newTRN.trnNumber || !newTRN.legalEntityName) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (newTRN.trnNumber.length !== 15) {
      toast.error('TRN must be exactly 15 digits');
      return;
    }

    const trnId = `trn-${String(trns.length + 1).padStart(3, '0')}`;
    const newTRNData: TRN = {
      id: trnId,
      trnNumber: newTRN.trnNumber,
      legalEntityName: newTRN.legalEntityName,
      status: newTRN.status,
      registrationDate: newTRN.registrationDate,
      branches: []
    };

    setTrns([...trns, newTRNData]);
    setIsAddTRNDialogOpen(false);
    toast.success(`TRN ${newTRN.trnNumber} added successfully`);
    
    // Reset form
    setNewTRN({
      trnNumber: '',
      legalEntityName: '',
      registrationDate: new Date().toISOString().split('T')[0],
      status: 'active'
    });
  };

  const handleAddBranch = () => {
    if (!newBranch.branchCode || !newBranch.branchName || !newBranch.address || !newBranch.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!selectedTRNId) {
      toast.error('No TRN selected');
      return;
    }

    const selectedTRN = trns.find(t => t.id === selectedTRNId);
    if (!selectedTRN) return;

    const branchId = `branch-${String(Date.now()).slice(-3)}`;
    const newBranchData: Branch = {
      id: branchId,
      branchCode: newBranch.branchCode,
      branchName: newBranch.branchName,
      address: newBranch.address,
      city: newBranch.city,
      emirate: newBranch.emirate,
      status: newBranch.status,
      manager: newBranch.manager || undefined
    };

    setTrns(trns.map(t => 
      t.id === selectedTRNId 
        ? { ...t, branches: [...t.branches, newBranchData] }
        : t
    ));

    setIsAddBranchDialogOpen(false);
    toast.success(`Branch ${newBranch.branchName} added successfully`);
    
    // Reset form
    setNewBranch({
      branchCode: '',
      branchName: '',
      address: '',
      city: '',
      emirate: 'Dubai',
      manager: '',
      status: 'active'
    });
    setSelectedTRNId(null);
  };

  const handleToggleTRNStatus = (trnId: string) => {
    setTrns(trns.map(t => 
      t.id === trnId 
        ? { ...t, status: t.status === 'active' ? 'suspended' : 'active' }
        : t
    ));
    const trn = trns.find(t => t.id === trnId);
    toast.success(`TRN ${trn?.trnNumber} ${trn?.status === 'active' ? 'suspended' : 'activated'}`);
  };

  const handleToggleBranchStatus = (trnId: string, branchId: string) => {
    setTrns(trns.map(t => 
      t.id === trnId 
        ? {
            ...t,
            branches: t.branches.map(b =>
              b.id === branchId
                ? { ...b, status: b.status === 'active' ? 'inactive' : 'active' }
                : b
            )
          }
        : t
    ));
    toast.success('Branch status updated');
  };

  const handleDeleteTRN = (trnId: string) => {
    const trn = trns.find(t => t.id === trnId);
    if (trn && trn.branches.length > 0) {
      toast.error('Cannot delete TRN with active branches. Remove all branches first.');
      return;
    }
    setTrns(trns.filter(t => t.id !== trnId));
    toast.success('TRN deleted successfully');
  };

  const handleDeleteBranch = (trnId: string, branchId: string) => {
    setTrns(trns.map(t =>
      t.id === trnId
        ? { ...t, branches: t.branches.filter(b => b.id !== branchId) }
        : t
    ));
    toast.success('Branch deleted successfully');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl text-gray-900">TRN & Branch Management</h2>
          <p className="text-gray-600 mt-1">Manage Tax Registration Numbers and Branches for {clientName}</p>
        </div>
        <Button 
          className="bg-[#0052CC] hover:bg-[#0041A3]"
          onClick={() => setIsAddTRNDialogOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New TRN
        </Button>
      </div>

      {/* TRN Cards */}
      <div className="space-y-4">
        {trns.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl text-gray-900 mb-2">No TRNs Added</h3>
              <p className="text-gray-600 mb-6">Add a Tax Registration Number to get started</p>
              <Button 
                className="bg-[#0052CC] hover:bg-[#0041A3]"
                onClick={() => setIsAddTRNDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First TRN
              </Button>
            </CardContent>
          </Card>
        ) : (
          trns.map((trn) => (
            <Card key={trn.id} className="premium-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#0052CC] to-indigo-600 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <CardTitle className="text-xl">{trn.legalEntityName}</CardTitle>
                        <Badge className={trn.status === 'active' ? 'bg-green-500' : 'bg-red-500'}>
                          {trn.status}
                        </Badge>
                      </div>
                      <p className="text-gray-600 mt-1">TRN: {trn.trnNumber}</p>
                      <p className="text-sm text-gray-500">Registered: {new Date(trn.registrationDate).toLocaleDateString('en-AE')}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedTRNId(trn.id);
                        setIsAddBranchDialogOpen(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Branch
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleTRNStatus(trn.id)}
                    >
                      {trn.status === 'active' ? (
                        <>
                          <XCircle className="w-4 h-4 mr-1" />
                          Suspend
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Activate
                        </>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteTRN(trn.id)}
                      disabled={trn.branches.length > 0}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Branches */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-gray-900">Branches ({trn.branches.length})</h4>
                  </div>
                  
                  {trn.branches.length === 0 ? (
                    <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <MapPin className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600 mb-4">No branches added yet</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedTRNId(trn.id);
                          setIsAddBranchDialogOpen(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Branch
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {trn.branches.map((branch) => (
                        <div
                          key={branch.id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-start space-x-3">
                            <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                            <div>
                              <div className="flex items-center space-x-2">
                                <p className="text-gray-900">{branch.branchName}</p>
                                <Badge variant="outline" className="text-xs">{branch.branchCode}</Badge>
                                {branch.status === 'active' ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                                ) : (
                                  <AlertCircle className="w-4 h-4 text-orange-500" />
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{branch.address}</p>
                              <p className="text-sm text-gray-500">{branch.city}, {branch.emirate}</p>
                              {branch.manager && (
                                <p className="text-sm text-gray-500">Manager: {branch.manager}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleBranchStatus(trn.id, branch.id)}
                            >
                              {branch.status === 'active' ? (
                                <XCircle className="w-4 h-4 text-orange-500" />
                              ) : (
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteBranch(trn.id, branch.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add TRN Dialog */}
      <Dialog open={isAddTRNDialogOpen} onOpenChange={setIsAddTRNDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Tax Registration Number</DialogTitle>
            <DialogDescription>
              Add a new TRN for {clientName}. You can add branches to this TRN after creation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>TRN Number <span className="text-red-500">*</span></Label>
              <Input 
                value={newTRN.trnNumber}
                onChange={(e) => setNewTRN({ ...newTRN, trnNumber: e.target.value })}
                placeholder="100123456700003"
                maxLength={15}
                className="mt-1"
              />
              <p className="text-xs text-gray-600 mt-1">Must be exactly 15 digits</p>
            </div>

            <div>
              <Label>Legal Entity Name <span className="text-red-500">*</span></Label>
              <Input 
                value={newTRN.legalEntityName}
                onChange={(e) => setNewTRN({ ...newTRN, legalEntityName: e.target.value })}
                placeholder="ABC Trading LLC"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Registration Date</Label>
              <Input 
                type="date"
                value={newTRN.registrationDate}
                onChange={(e) => setNewTRN({ ...newTRN, registrationDate: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Status</Label>
              <Select 
                value={newTRN.status} 
                onValueChange={(value: 'active' | 'inactive' | 'suspended') => 
                  setNewTRN({ ...newTRN, status: value })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddTRNDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddTRN} className="bg-[#0052CC] hover:bg-[#0041A3]">
              Add TRN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Branch Dialog */}
      <Dialog open={isAddBranchDialogOpen} onOpenChange={setIsAddBranchDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Branch</DialogTitle>
            <DialogDescription>
              Add a new branch location for {trns.find(t => t.id === selectedTRNId)?.legalEntityName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Branch Code <span className="text-red-500">*</span></Label>
                <Input 
                  value={newBranch.branchCode}
                  onChange={(e) => setNewBranch({ ...newBranch, branchCode: e.target.value })}
                  placeholder="DXB-001"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Branch Name <span className="text-red-500">*</span></Label>
                <Input 
                  value={newBranch.branchName}
                  onChange={(e) => setNewBranch({ ...newBranch, branchName: e.target.value })}
                  placeholder="Dubai Main Office"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Address <span className="text-red-500">*</span></Label>
              <Input 
                value={newBranch.address}
                onChange={(e) => setNewBranch({ ...newBranch, address: e.target.value })}
                placeholder="Sheikh Zayed Road, Trade Centre 1"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City <span className="text-red-500">*</span></Label>
                <Input 
                  value={newBranch.city}
                  onChange={(e) => setNewBranch({ ...newBranch, city: e.target.value })}
                  placeholder="Dubai"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Emirate <span className="text-red-500">*</span></Label>
                <Select 
                  value={newBranch.emirate} 
                  onValueChange={(value) => setNewBranch({ ...newBranch, emirate: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UAE_EMIRATES.map(emirate => (
                      <SelectItem key={emirate} value={emirate}>{emirate}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Branch Manager (Optional)</Label>
              <Input 
                value={newBranch.manager}
                onChange={(e) => setNewBranch({ ...newBranch, manager: e.target.value })}
                placeholder="Ahmed Al Mansoori"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Status</Label>
              <Select 
                value={newBranch.status} 
                onValueChange={(value: 'active' | 'inactive') => 
                  setNewBranch({ ...newBranch, status: value })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsAddBranchDialogOpen(false);
              setSelectedTRNId(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddBranch} className="bg-[#0052CC] hover:bg-[#0041A3]">
              Add Branch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
